import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Fuel, Eye, EyeOff, Users } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RegisterPage = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Accept both ?code= (from email invite link) and ?invite= (legacy)
  const inviteCodeFromUrl = searchParams.get('code') || searchParams.get('invite');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    company_name: '',
    invite_code: inviteCodeFromUrl || ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [validatingInvite, setValidatingInvite] = useState(false);
  const [agreed, setAgreed] = useState(false);

  // Validate invite code when it changes; auto-prefill name/email when invite metadata contains them
  useEffect(() => {
    const validateInvite = async () => {
      if (formData.invite_code && formData.invite_code.length >= 8) {
        setValidatingInvite(true);
        try {
          const response = await axios.get(`${API_URL}/api/auth/validate-invite/${formData.invite_code}`);
          setInviteInfo(response.data);
          // Prefill name/email if invite pre-specifies them
          setFormData((prev) => ({
            ...prev,
            name: prev.name || response.data.invitee_name || '',
            email: prev.email || response.data.invitee_email || '',
          }));
        } catch (error) {
          setInviteInfo(null);
        } finally {
          setValidatingInvite(false);
        }
      } else {
        setInviteInfo(null);
      }
    };
    validateInvite();
  }, [formData.invite_code]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.password) {
      toast.error('Please fill in required fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (!agreed) {
      toast.error('Please accept the Terms of Service and Privacy Policy to continue');
      return;
    }

    setLoading(true);
    try {
      const result = await register(
        formData.name, 
        formData.email, 
        formData.password, 
        formData.company_name,
        formData.invite_code || null
      );
      if (result?.verificationRequired) {
        toast.success('Check your inbox for a 6-digit code');
        navigate(`/verify-email?email=${encodeURIComponent(result.email)}`);
        return;
      }
      if (result?.emailVerification?.sent === false) {
        toast.warning("Welcome! We couldn't send your confirmation email just now — use \"Send new code\" at the top of the app.");
      } else if (result?.emailVerification) {
        toast.success('Welcome to FleetCost.App! We emailed you a 6-digit code to confirm your email (check spam/junk).');
      } else {
        toast.success('Welcome to FleetCost.App!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const ACCESS_LEVEL_LABELS = {
    full_access: 'Full Access',
    view_entry: 'View + Entry',
    data_entry: 'Data Entry Only'
  };

  return (
    <div className="min-h-screen flex bg-white">
      {/* Left - Hero with truck photo */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-slate-950">
        <img
          src="https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80"
          alt="Scania fleet truck"
          className="absolute inset-0 w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/85 via-slate-950/60 to-slate-950/85" />
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
        }} />

        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white rounded-sm flex items-center justify-center overflow-hidden">
              <img src="/fleetcost-icon-192.png" alt="FleetCost.App" className="w-8 h-8 object-contain" data-testid="register-brand-icon" />
            </div>
            <div className="flex flex-col leading-none">
              <span className="font-display font-black text-2xl tracking-tight" data-testid="register-brand-name">
                FleetCost<span className="text-blue-400">.App</span>
              </span>
              <span className="text-[10px] font-mono text-slate-400 tracking-[0.25em] uppercase mt-1">Fleet Operating System</span>
            </div>
          </div>

          <div className="max-w-md">
            <p className="text-xs font-mono text-slate-400 tracking-[0.2em] uppercase mb-4">
              [ New Operator / Onboarding ]
            </p>
            <h1 className="font-display text-5xl xl:text-6xl font-black mb-6 tracking-tight leading-[0.95]">
              Start managing<br />smarter today.
            </h1>
            <p className="text-slate-300 text-base leading-relaxed max-w-sm">
              Join fleet operators across South Africa who&apos;ve streamlined operations
              and slashed costs with FleetCost.App.
            </p>
            <div className="mt-10 border border-white/10 divide-y divide-white/10">
              <div className="p-3 flex items-center gap-3">
                <span className="font-mono text-[10px] text-slate-500 w-8">01</span>
                <span className="text-slate-300 text-sm">Track fuel, maintenance & expenses</span>
              </div>
              <div className="p-3 flex items-center gap-3">
                <span className="font-mono text-[10px] text-slate-500 w-8">02</span>
                <span className="text-slate-300 text-sm">Manage drivers & vehicle lifecycle</span>
              </div>
              <div className="p-3 flex items-center gap-3">
                <span className="font-mono text-[10px] text-slate-500 w-8">03</span>
                <span className="text-slate-300 text-sm">AI-powered invoice capture & alerts</span>
              </div>
            </div>
          </div>

          <p className="text-slate-500 text-[10px] font-mono tracking-[0.2em] uppercase">
            © 2026 FleetCost.App — Built for fleet managers
          </p>
        </div>
      </div>

      {/* Right - Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-slate-50">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <img src="/fleetcost-logo.png" alt="FleetCost.App" className="h-10 object-contain" data-testid="register-mobile-logo" />
          </div>

          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="px-8 pt-8 pb-6 border-b border-slate-100">
              <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-2">
                [ {inviteInfo ? 'Join Team' : 'New Account'} ]
              </p>
              <h2 className="font-display text-3xl font-black text-slate-950 tracking-tight">
                {inviteInfo ? 'Join a team' : 'Create account'}
              </h2>
              <p className="text-slate-500 text-sm mt-2">
                {inviteInfo
                  ? `You've been invited to join ${inviteInfo.company_name || 'a team'}.`
                  : 'Get started with FleetCost.App in minutes.'}
              </p>
            </div>
            <div className="p-8">
              {inviteInfo && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-sm">
                  <div className="flex items-start gap-3">
                    <Users className="w-5 h-5 text-blue-700 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-blue-900">
                        Joining {inviteInfo.company_name || 'Team'}
                      </p>
                      <p className="text-xs text-blue-700 mt-1 font-mono">
                        Invited by {inviteInfo.invited_by} • {ACCESS_LEVEL_LABELS[inviteInfo.access_level]}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="label-eyebrow flex items-center gap-2">
                    Invite Code
                    <span className="text-[10px] text-slate-400 font-normal normal-case tracking-normal">(optional)</span>
                  </Label>
                  <Input
                    type="text"
                    placeholder="BIQ-XXXXXXXX"
                    value={formData.invite_code}
                    onChange={(e) => setFormData({ ...formData, invite_code: e.target.value.toUpperCase() })}
                    className={`h-11 font-mono ${inviteInfo ? 'border-blue-500 bg-blue-50' : ''}`}
                    data-testid="register-invite-input"
                  />
                  {validatingInvite && (
                    <p className="text-[11px] font-mono uppercase tracking-wider text-slate-500">Validating…</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="label-eyebrow">Full Name *</Label>
                  <Input
                    type="text"
                    placeholder="John Smith"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="h-11"
                    data-testid="register-name-input"
                  />
                </div>
                {!inviteInfo && (
                  <div className="space-y-2">
                    <Label className="label-eyebrow">Company Name</Label>
                    <Input
                      type="text"
                      placeholder="ABC Transport"
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      className="h-11"
                      data-testid="register-company-input"
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label className="label-eyebrow">Email *</Label>
                  <Input
                    type="email"
                    placeholder="you@company.co.za"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="h-11"
                    data-testid="register-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-eyebrow">Password *</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="h-11 pr-10"
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="label-eyebrow">Confirm Password *</Label>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className="h-11"
                    data-testid="register-confirm-password-input"
                  />
                </div>
                <label className="flex items-start gap-2 text-xs text-slate-600 select-none cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 shrink-0"
                    data-testid="register-consent-checkbox"
                    required
                  />
                  <span>
                    I agree to the{' '}
                    <Link to="/terms" target="_blank" className="text-indigo-600 hover:underline" data-testid="register-terms-link">
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link to="/privacy" target="_blank" className="text-indigo-600 hover:underline" data-testid="register-privacy-link">
                      Privacy Policy
                    </Link>
                    , and I consent to Fleet Cost processing my personal information as described.
                  </span>
                </label>
                <Button
                  type="submit"
                  className="w-full h-11"
                  disabled={loading || !agreed}
                  data-testid="register-submit-btn"
                >
                  {loading ? 'Creating account…' : 'Create account →'}
                </Button>
              </form>
              <div className="mt-6 pt-6 border-t border-slate-100 text-center">
                <p className="text-sm text-slate-500">
                  Already have an account?{' '}
                  <Link
                    to="/login"
                    className="text-slate-950 font-semibold hover:underline"
                    data-testid="login-link"
                  >
                    Sign in →
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
